import { fromZodError } from 'zod-validation-error';
import debug from 'debug';
import { ZodError } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { datadogRum } from '@datadog/browser-rum-slim';
import { getQueryKeyForStudio } from 'state/utils/getQueryKeyForStudio';
import { LocaleEnum } from 'schema/locale';
import { CommonContent, commonContentDataSchema } from 'schema/studio/commonContentSchema';
import { sanityFetch } from 'sanity-client';
import { MONITORING_TAGS } from 'constants/monitoring';
import { CommonContentDataServiceOptions, CommonContentDataServiceQueryOptions } from './types';

const log = debug('ag:state:studio:commonContent');

const query = `*[_type == "commonContent" && language == $locale][0]`;

async function commonContentDataService({
  locale = LocaleEnum.enUS
}: CommonContentDataServiceOptions) {
  let localeParam = locale;

  if (locale === LocaleEnum.enQa) {
    localeParam = LocaleEnum.enUS;
  } else if (locale === LocaleEnum.enAud) {
    localeParam = LocaleEnum.enAU;
  } else if (locale === LocaleEnum.enNzd) {
    localeParam = LocaleEnum.enNZ;
  }

  let content;

  try {
    content = await sanityFetch(query, {
      locale: localeParam
    });

    return commonContentDataSchema.parse(content);
  } catch (error) {
    const tags = {
      [MONITORING_TAGS.SANITY_CONTENT]: true
    };

    const isValidationError = error instanceof ZodError;
    const parsedError = isValidationError ? fromZodError(error) : error;
    log(parsedError);
    datadogRum.addError(parsedError, { tags });
    // eslint-disable-next-line no-console
    console.error('Error with Common Content:', parsedError);

    if (!isValidationError) {
      throw error;
    }
  }

  return content as CommonContent;
}

export function useQueryGetCommonContentData({ locale }: CommonContentDataServiceQueryOptions) {
  return useQuery({
    queryKey: getQueryKeyForStudio({ keys: ['commonContent', locale] }),
    queryFn: () => commonContentDataService({ locale }),
    staleTime: Infinity
  });
}
